import type { HTMLAttributeAnchorTarget } from "react";
import {
  type MouseEventHandler,
  type ReactNode,
  forwardRef,
  useEffect,
  useRef,
} from "react";
import { Link } from "react-router";
import DCircleLoader from "@UI/components/Atoms/Loader/DCircleLoader.tsx";
import { isString, isUndefined } from "@UI/utils/guards.ts";
import classNames from "classnames";

interface Props {
  className?: string;
  size?: "2x-small" | "x-small" | "medium";
  disabled?: boolean;
  label?: string | ReactNode;
  rounded?: boolean;
  fullWidth?: boolean;
  form?: string;
  href?: string;
  target?: HTMLAttributeAnchorTarget;
  variant?: "primary" | "secondary" | "ai" | "error" | "success" | "ghost";
  type?: "submit" | "button";
  loading?: boolean;
  prepend?: ReactNode;
  append?: ReactNode;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  autoFocus?: boolean;
}

const DButton = forwardRef<HTMLButtonElement, Props>(
  (
    {
      className,
      size = "medium",
      disabled = false,
      rounded,
      fullWidth,
      form,
      href,
      target,
      label,
      variant = "primary",
      type = "button",
      prepend,
      append,
      loading,
      onClick,
      autoFocus,
    },
    ref,
  ) => {
    const internalRef = useRef<HTMLButtonElement | null>(null);

    const classes = classNames(
      `d-button d-button--${variant} d-button--${size} d-flex d-align-center d-justify-center`,
      {
        "d-button--rounded": rounded,
        "d-button--full-width": fullWidth,
      },
      className,
    );

    useEffect(() => {
      if (isUndefined(loading)) return;

      if (internalRef.current && isString(label)) {
        if (loading) {
          internalRef.current.style.minWidth = `${Math.max(94, internalRef.current.offsetWidth)}px`;
        } else {
          internalRef.current.style.minWidth = "unset";
        }
      }
    }, [loading, label]);

    const handleClick: MouseEventHandler<HTMLButtonElement> = event => {
      if (loading) {
        event.preventDefault();
        return;
      }
      onClick?.(event);
    };

    const content = (
      <>
        {loading && (
          <DCircleLoader
            color={
              variant === "secondary" || variant === "ghost"
                ? "primary"
                : "light"
            }
            height="24px"
            width="24px"
          />
        )}
        {!loading && (
          <>
            {!rounded && prepend && prepend}
            {isString(label) && (
              <span className="d-label-s-medium d-text-3-dots">{label}</span>
            )}
            {!isString(label) && label}
            {!rounded && append && append}
          </>
        )}
      </>
    );

    if (href && !disabled) {
      return (
        <Link
          to={href}
          target={target}
          rel={target === "_blank" ? "noopener noreferrer" : undefined}
          className={classes}
          ref={node => {
            internalRef.current = node as unknown as HTMLButtonElement;
            if (typeof ref === "function")
              ref(node as unknown as HTMLButtonElement);
            else if (ref) ref.current = node as unknown as HTMLButtonElement;
          }}
          style={{ textDecoration: "none" }}
        >
          {content}
        </Link>
      );
    }

    return (
      <button
        autoFocus={autoFocus}
        className={classes}
        disabled={disabled}
        form={form}
        onClick={handleClick}
        ref={node => {
          internalRef.current = node;
          if (typeof ref === "function") ref(node);
          else if (ref) ref.current = node;
        }}
        type={type}
      >
        {content}
      </button>
    );
  },
);

DButton.displayName = "DButton";

export default DButton;
